/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
/* global globalThis */
import React, { useState, useEffect, useId } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon as MuiListItemIcon,
  ListItemText,
  Toolbar,
  Box,
  Divider,
  Collapse,
  IconButton,
  styled,
} from "@mui/material";
import MenuToggleIcon from "@mui/icons-material/Menu";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ExpandLess from "@mui/icons-material/ExpandLess";

import { ReactComponent as DashboardIcon } from "../../../assets/Dashboard.svg";
import { ReactComponent as UserManagementIcon } from "../../../assets/User Management.svg";
import { ReactComponent as ProviderOutreachIcon } from "../../../assets/Provider Outreach.svg";
import { ReactComponent as PharmacyOutreachIcon } from "../../../assets/Pharmacy Outreach.svg";
import { ReactComponent as ManualTasksIcon } from "../../../assets/Manual Tasks.svg";
import { ReactComponent as ReportingIcon } from "../../../assets/Reporting.svg";
import { usePermissions } from "../../../redux/permissions";
import { ReactComponent as memberIcon } from "../../../assets/Members.svg";
import { ReactComponent as CagIcon } from "../../../assets/CagIcon.svg";
import { ReactComponent as CgAdminIcon } from "../../../assets/CgAdmin.svg";

function findActiveNavState(path, menuItems) {
  for (const item of menuItems) {
    if (!item.isClickable) continue;
    if (item.subItems) {
      const matchingSubItem = item.subItems.find(
        (subItem) => subItem.path === path,
      );
      if (matchingSubItem) {
        return {
          activeItem: item.text,
          activeSubItem: matchingSubItem.text,
          openSubmenus: { [item.text]: true },
        };
      }
    } else if (item.path === path) {
      return {
        activeItem: item.text,
        activeSubItem: "",
        openSubmenus: {},
      };
    }
  }
  return null;
}

const StyledMenuIcon = styled("div", {
  shouldForwardProp: (prop) => prop !== "isActive",
})(({ isActive }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "20px",
  height: "20px",

  "&.members-icon": {
    width: "25px",
    height: "25px",
  },
  "& svg": {
    width: "100%",
    height: "100%",
    "& path, & rect, & circle, & polygon, & line, & polyline": {
      fill: isActive ? "#403C6F" : "none",
      stroke: isActive ? "#403C6F" : "#FFFFFF",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      transition: "all 0.2s ease-in-out",
    },
  },

  "&.members-icon svg path, &.members-icon svg rect, &.members-icon svg circle, &.members-icon svg polygon, &.members-icon svg line, &.members-icon svg polyline":
    {
      strokeWidth: isActive ? "1" : "1.2",
    },

  "&:not(.members-icon) svg path, &:not(.members-icon) svg rect, &:not(.members-icon) svg circle, &:not(.members-icon) svg polygon, &:not(.members-icon) svg line, &:not(.members-icon) svg polyline":
    {
      strokeWidth: isActive ? "1" : "1.5",
    },
}));

const NavigationMenuIcon = ({ src: IconComponent, isActive, text }) => (
  <StyledMenuIcon
    isActive={isActive}
    className={
      text === "Members" || text === "Member Outreach" ? "members-icon" : ""
    }
  >
    <IconComponent />
  </StyledMenuIcon>
);

NavigationMenuIcon.propTypes = {
  src: PropTypes.elementType.isRequired,
  isActive: PropTypes.bool,
  text: PropTypes.string,
};

const ListItemIcon = styled(MuiListItemIcon)({
  minWidth: "30px",
  marginRight: "8px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const drawerWidth = 280;
const collapsedDrawerWidth = 76;

const menuItems = [
  {
    text: "Dashboard",
    icon: (
      <NavigationMenuIcon
        src={DashboardIcon}
        isActive={false}
        text="Dashboard"
      />
    ),
    path: "/dashboard",
    isClickable: true,
  },
  {
    text: "Member Outreach",
    icon: (
      <NavigationMenuIcon
        src={memberIcon}
        isActive={false}
        text="Member Outreach"
      />
    ),
    path: "/members/member-outreach",
    isClickable: true,
  },
  {
    text: "Provider Outreach",
    icon: (
      <NavigationMenuIcon
        src={ProviderOutreachIcon}
        isActive={false}
        text="Provider Outreach"
      />
    ),
    path: "/provider-outreach",
    isClickable: true,
  },
  {
    text: "Pharmacy Outreach",
    icon: (
      <NavigationMenuIcon
        src={PharmacyOutreachIcon}
        isActive={false}
        text="Pharmacy Outreach"
      />
    ),
    path: "/pharmacy-outreach",
    isClickable: true,
  },
  {
    text: "Manual Tasks",
    icon: (
      <NavigationMenuIcon
        src={ManualTasksIcon}
        isActive={false}
        text="Manual Tasks"
      />
    ),
    path: "/manual-tasks",
    isClickable: true,
  },
  {
    text: "Reporting",
    icon: (
      <NavigationMenuIcon
        src={ReportingIcon}
        isActive={false}
        text="Reporting"
      />
    ),
    subItems: [
      { text: "Member Reporting", path: "/reporting/member-reporting" },
      { text: "Supervisor Reporting", path: "/reporting/supervisor-reporting" },
      { text: "Invoice Reporting", path: "/reporting/invoice-reporting" },
    ],
    isClickable: true,
  },
  {
    text: "Role Management",
    icon: (
      <NavigationMenuIcon
        src={UserManagementIcon}
        isActive={false}
        text="Role Management"
      />
    ),
    path: "/role-management",
    isClickable: true,
  },
  {
    text: "CAG Configuration",
    icon: <CagIcon />,
    path: "/cag-configuration",
    isClickable: true,
  },
  {
    text: "CG+ Assessment Templates",
    icon: <CgAdminIcon />,
    path: "/cg-config",
    isClickable: true,
  },
];

// PropTypes shapes for navigation items
const SubNavItemPropType = PropTypes.shape({
  text: PropTypes.string.isRequired,
  path: PropTypes.string,
});

const NavItemPropType = PropTypes.shape({
  text: PropTypes.string.isRequired,
  icon: PropTypes.element.isRequired,
  path: PropTypes.string,
  isClickable: PropTypes.bool,
  subItems: PropTypes.arrayOf(SubNavItemPropType),
});
export const AppLogo = () => (
  <svg
    aria-hidden="true"
    width="100"
    height="30"
    viewBox="0 0 100 56"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M43.5936 33.3168C44.3806 34.1173 45.5612 34.5578 47.1371 34.6417C48.5871 34.7186 50.0947 34.5001 51.6601 33.988C53.2238 33.4759 54.6108 32.8204 55.8158 32.0234C56.3318 31.7228 56.7341 31.354 57.0279 30.9188C57.3218 30.4836 57.4757 30.1235 57.4914 29.8369C57.5124 29.4279 57.3375 29.2146 56.965 29.1937C56.7988 29.1849 56.5784 29.2251 56.3038 29.3125C56.0292 29.3999 55.7161 29.5992 55.3681 29.9085C54.9361 30.2144 54.3029 30.5902 53.4686 31.0394C52.6343 31.4868 51.7108 31.8591 50.6982 32.1545C49.6855 32.4499 48.6815 32.5705 47.6863 32.5181C46.8573 32.4744 46.2469 32.2262 45.8568 31.7752C45.465 31.3243 45.2901 30.7527 45.3269 30.0571C45.3514 29.6079 45.5105 28.9699 45.8044 28.145C46.0982 27.32 46.4777 26.4583 46.9412 25.5599C47.4047 24.6615 47.8612 23.8977 48.3107 23.265C49.1188 22.1604 50.0563 21.1326 51.1267 20.1853C52.1953 19.238 53.285 18.4846 54.3956 17.9288C55.5063 17.373 56.4542 17.1143 57.2413 17.1563C57.4477 17.1668 57.6121 17.1965 57.7345 17.2437C58.0616 17.3433 58.212 17.6177 58.1893 18.0669C58.1613 18.5983 57.9269 19.2624 57.4897 20.0595C57.0507 20.8565 56.7218 21.3721 56.5032 21.6063C56.2409 21.8388 56.1027 22.0992 56.0869 22.3841C56.0555 22.9574 56.3301 23.258 56.9107 23.2877C57.2833 23.3069 57.6244 23.1409 57.9339 22.7878C58.5024 22.2443 59.0323 21.4927 59.5203 20.5349C60.0083 19.577 60.2776 18.6472 60.3249 17.7488C60.3616 17.0549 60.1972 16.4502 59.8299 15.9398C59.4626 15.4277 58.7612 15.1445 57.7258 15.0903C55.8193 14.989 53.9426 15.5063 52.0956 16.6389C50.2469 17.7715 48.6116 19.203 47.1896 20.9316C46.5232 21.7164 45.8393 22.655 45.1362 23.7456C44.4331 24.8363 43.8332 25.9532 43.3347 27.0945C43.2368 27.32 43.1493 27.542 43.0689 27.7639C43.0024 27.7919 42.9412 27.8199 42.8905 27.8408C42.1051 28.5382 41.2131 29.2706 40.2162 30.0379C39.2175 30.8052 38.1925 31.326 37.1396 31.5969C36.886 31.6669 36.6761 31.6966 36.5117 31.6878C35.973 31.6599 35.7194 31.3383 35.7526 30.7248L35.7596 30.6024C35.8104 29.6621 36.029 28.6798 36.4173 27.6538C36.8056 26.6278 37.1676 25.6735 37.5069 24.7891C37.7448 24.2281 38.0981 23.4677 38.5651 22.5082C39.0303 21.5486 39.4676 20.6083 39.8699 19.6854C40.2721 18.7643 40.4873 18.0791 40.5118 17.6282C40.5695 17.3031 40.5345 17.0147 40.4033 16.7613C40.2704 16.5078 40.0395 16.3733 39.709 16.3558C39.4186 16.34 39.1755 16.4204 38.9796 16.5935C38.7837 16.7683 38.5948 16.9728 38.4164 17.2087C37.8182 18.2836 37.2393 19.3708 36.6814 20.4684C36.1234 21.5678 35.4448 22.6078 34.6437 23.5901C34.1977 24.1407 33.5663 24.866 32.7495 25.7661C31.931 26.6663 31.0215 27.5612 30.0175 28.4508C29.0136 29.3405 28.0184 30.0886 27.0302 30.6916C26.0402 31.2963 25.1745 31.5777 24.4294 31.5375C23.7665 31.5026 23.324 31.2247 23.1036 30.6986C22.8832 30.1742 22.7888 29.6271 22.8185 29.0538C22.8937 28.4019 23.1648 27.6276 23.6301 26.7292C24.0935 25.8308 24.599 24.9569 25.143 24.104C25.6869 23.251 26.1399 22.5484 26.5037 21.9926C26.8168 21.5591 28.7128 18.9268 29.3004 18.2277C29.8881 17.5268 30.1942 16.9728 30.2222 16.5638C30.2379 16.3191 30.1924 16.0796 30.084 15.8472C29.9756 15.6147 29.7744 15.4888 29.4858 15.4696C28.9471 15.4347 28.5186 15.6322 28.1985 16.0621C28.0639 16.2177 27.9379 16.3837 27.8225 16.5603C27.7053 16.7368 27.5794 16.9238 27.4412 17.1196C26.9847 17.7051 25.5138 19.9301 25.1465 20.5663C24.153 22.031 23.1875 23.5586 22.2501 25.1492C21.3126 26.7397 20.7949 28.4351 20.6969 30.2336C20.6357 31.3505 20.9208 32.1353 21.8041 32.8641C23.2173 34.0317 24.7127 33.8639 26.3271 33.2976C27.5129 32.8676 28.683 32.172 29.8374 31.2072C30.99 30.2441 32.0762 29.1727 33.0958 27.9964C34.1138 26.8201 34.9831 25.7382 35.7019 24.7507C35.2262 25.8745 34.7277 27.2011 34.21 28.7305C33.6905 30.2616 33.4002 31.5987 33.339 32.7435C33.3145 33.1927 33.3739 33.6157 33.5191 34.0142C33.6625 34.411 34.0053 34.6242 34.544 34.6522C35.2069 34.6871 36.0482 34.4949 37.0679 34.0789C38.0876 33.6611 39.0688 33.1595 40.015 32.574C40.9595 31.9885 41.6714 31.5026 42.1506 31.118C42.2678 31.0254 42.385 30.9223 42.5039 30.8139C42.5494 31.791 42.9097 32.6264 43.5901 33.3151"
      fill="white"
    />
    <path
      d="M68.1483 43.0961C66.4762 45.2005 64.5785 47.0479 62.4569 48.6385C60.3353 50.2273 58.0476 51.4298 55.5937 52.2443C54.9168 52.4942 53.9251 52.7721 52.6203 53.071C51.3173 53.3716 50.0632 53.4905 48.8616 53.4276C47.5778 53.3594 46.6806 53.0553 46.1681 52.5169C45.6574 51.9768 45.4213 51.3406 45.4615 50.6048C45.5087 49.7466 45.8375 48.8412 46.4497 47.8886C47.0619 46.9378 47.7807 46.1338 48.6045 45.4801C50.7733 43.79 53.173 42.4511 55.8018 41.4618C58.4306 40.4726 61.1574 39.6109 63.9803 38.8785C66.8032 38.1444 69.5685 37.3177 72.2777 36.3948C71.1933 38.7562 69.8168 40.9899 68.1448 43.0961M79.6324 30.5676C78.6512 30.9224 76.8882 32.2297 75.9717 32.6545C76.5734 30.7598 77.1016 28.8494 77.5598 26.9268C78.0163 25.0042 78.2962 23.1217 78.3959 21.2813C78.4151 20.9142 78.3154 20.6223 78.1003 20.4039C77.8834 20.1889 77.6298 20.0718 77.3395 20.056C76.883 20.0316 76.4999 20.2168 76.1886 20.6101C75.6954 21.2813 74.8803 22.1395 73.7434 23.1864C72.6066 24.2334 71.3752 25.2978 70.0495 26.3745C68.7237 27.4529 67.4556 28.4107 66.2488 29.2497C65.0402 30.0886 64.1167 30.6549 63.4766 30.9486C62.9659 31.1671 62.5863 31.2702 62.338 31.2579C61.8412 31.2317 61.6121 30.8507 61.6523 30.1149C61.6961 29.2969 62.0056 28.381 62.5793 27.3655C63.153 26.35 63.8526 25.31 64.6799 24.2474C65.5055 23.1847 66.2768 22.1797 66.9921 21.2323C67.7967 20.1679 68.3599 19.1821 68.6782 18.2767C68.9965 17.3714 69.1732 16.611 69.2064 15.9976C69.2379 15.426 69.1784 14.9716 69.0298 14.636C68.7622 14.213 68.4194 13.9893 68.0066 13.9666C67.6743 13.9491 67.3769 14.0872 67.1111 14.3808C66.847 14.6744 66.735 15.1918 66.7788 15.9311C66.8417 16.3034 66.6983 16.8295 66.3503 17.5077C66.0022 18.1876 65.6227 18.843 65.2151 19.4775C64.8076 20.112 64.512 20.5682 64.3319 20.8461C63.3524 22.0661 62.4569 23.2581 61.6453 24.4256C60.8338 25.5932 60.1674 26.7765 59.6462 27.979C59.3576 28.7026 59.1949 29.4105 59.1565 30.1061C59.1075 31.0063 59.2929 31.784 59.7162 32.4412C60.1377 33.0984 60.8041 33.4515 61.7153 33.5004C62.4604 33.5389 63.3034 33.3379 64.2409 32.8957C65.2624 32.457 66.4499 31.8033 67.8055 30.9311C69.161 30.0607 70.5339 29.0452 71.9279 27.8881C73.3202 26.731 74.6092 25.5495 75.7968 24.34C75.5484 25.8449 75.1934 27.4144 74.7316 29.0504C74.2699 30.6864 73.7679 32.249 73.231 33.7381C71.1496 34.5719 69.0018 35.299 66.7875 35.9212C64.5732 36.5417 62.3362 37.1622 60.0817 37.7809C57.8254 38.3996 55.6252 39.1547 53.4773 40.0461C51.3313 40.9375 49.2849 42.0893 47.3382 43.5051C46.1314 44.3423 45.1274 45.4085 44.3299 46.6984C43.5341 47.9883 43.102 49.2467 43.0356 50.4737C42.9674 51.7409 43.3644 52.8683 44.2232 53.8575C45.0837 54.8451 46.5914 55.3974 48.7444 55.5092C49.8638 55.5687 51.0199 55.5057 52.2145 55.324C53.4091 55.1404 54.4708 54.9097 55.3995 54.6301C58.567 53.6897 61.439 52.2215 64.0188 50.2255C66.5986 48.2295 68.8409 45.9293 70.7491 43.3233C72.6555 40.7173 74.0985 38.0466 75.0779 35.3112C76.5122 34.7187 78.9748 33.3536 80.4475 32.5374C81.4724 31.9711 81.0667 30.0467 79.6289 30.5658"
      fill="white"
    />
    <path
      d="M25.2531 37.274C24.6182 36.7916 23.8679 36.4246 23.0039 36.1746C22.1398 35.9247 21.3213 35.7621 20.5482 35.6852C19.8573 35.645 19.1787 35.6136 18.5106 35.5909C17.8407 35.5699 17.1638 35.5769 16.4782 35.6171C14.5438 35.7272 12.5883 36.0383 10.6172 36.5487C8.64425 37.0608 6.49119 37.7617 4.15622 38.6531L25.0292 2.26304C25.1359 2.01834 25.1849 1.79637 25.1727 1.59537C25.1534 1.27726 25.017 1.0046 24.7616 0.780872C24.5063 0.555401 24.2159 0.452278 23.8941 0.471504C23.4918 0.494226 23.1788 0.691732 22.9584 1.06402L0.203399 40.553C0.0529813 40.761 -0.0134821 41.0039 0.00225919 41.2818C0.0232476 41.6401 0.18241 41.9495 0.481496 42.2134C0.778832 42.4756 1.07092 42.5997 1.35252 42.5822C1.71457 42.5613 1.98742 42.3865 2.17107 42.0561L2.50163 41.4986C5.07097 40.4743 7.48114 39.6581 9.73041 39.0516C11.9797 38.4451 14.1537 38.0815 16.2508 37.9627C16.856 37.9277 17.4612 37.9137 18.0681 37.919C18.675 37.9242 19.2837 37.9504 19.8923 37.9959C20.1757 38.0204 20.6462 38.1025 21.302 38.2441C21.9562 38.3874 22.5771 38.6111 23.1613 38.917C23.7454 39.2229 24.0515 39.6144 24.0795 40.0916C24.0918 40.2908 24.0603 40.4516 23.9868 40.5757C23.8801 40.8222 23.8311 41.0232 23.8399 41.1822C23.8696 41.6996 24.188 41.9408 24.7931 41.9058C25.2776 41.8778 25.6449 41.5964 25.8968 41.0651C26.1836 40.4499 26.3113 39.8434 26.2763 39.2473C26.2273 38.4119 25.8863 37.7529 25.2514 37.2705"
      fill="white"
    />
    <path
      d="M88.6085 22.2899V17.7612H91.8897C92.6575 17.7612 93.254 17.9605 93.6772 18.359C94.1005 18.7575 94.3121 19.3098 94.3121 20.0177C94.3121 20.7255 94.1005 21.2796 93.6772 21.6851C93.254 22.0906 92.6575 22.2916 91.8897 22.2916H88.6085V22.2899ZM97.1823 28.6188L99.5225 25.2612V25.1441H97.4936L96.0822 27.1716L93.574 23.8717C94.1932 23.6899 94.7267 23.3945 95.1709 22.975C95.951 22.2374 96.3428 21.2499 96.3428 20.0177C96.3428 18.7854 95.9527 17.7979 95.1709 17.0586C94.3891 16.3192 93.3344 15.9504 92.0069 15.9504H86.6951V29.4403H88.6085V24.0831H91.4332L94.9488 28.6695L92.4109 32.3662V32.4833H94.4695L96.0559 30.1149L97.8714 32.4833H100V32.3295L97.1806 28.6205L97.1823 28.6188Z"
      fill="white"
    />
  </svg>
);

function getScreenInches() {
  const { width, height } = globalThis.screen;
  const dpi = globalThis.devicePixelRatio * 96;
  const widthInInches = width / dpi;
  const heightInInches = height / dpi;
  return Math.hypot(widthInInches, heightInInches);
}

function persistNavState(activeItem, activeSubItem, openSubmenus) {
  try {
    localStorage.setItem("nav_activeItem", JSON.stringify(activeItem));
    localStorage.setItem("nav_activeSubItem", JSON.stringify(activeSubItem));
    localStorage.setItem("nav_openSubmenus", JSON.stringify(openSubmenus));
  } catch (error) {
    console.warn("navigation state save failed", error);
  }
}

function buildNavStateForPath(path) {
  const map = {
    "/members/member-outreach": {
      activeItem: "Member Outreach",
      activeSubItem: "",
      openSubmenus: {},
    },
    "/dashboard": {
      activeItem: "Dashboard",
      activeSubItem: "",
      openSubmenus: {},
    },
    "/manual-tasks": {
      activeItem: "Manual Tasks",
      activeSubItem: "",
      openSubmenus: {},
    },
    "/provider-outreach": {
      activeItem: "Provider Outreach",
      activeSubItem: "",
      openSubmenus: {},
    },
    "/pharmacy-outreach": {
      activeItem: "Pharmacy Outreach",
      activeSubItem: "",
      openSubmenus: {},
    },
    "/role-management": {
      activeItem: "Role Management",
      activeSubItem: "",
      openSubmenus: {},
    },
    "/cag-configuration": {
      activeItem: "CAG Configuration",
      activeSubItem: "",
      openSubmenus: {},
    },
  };
  return map[path];
}

function applyMembersHighlight(
  setActiveItem,
  setActiveSubItem,
  setOpenSubmenus,
) {
  const state = {
    activeItem: "Member Outreach",
    activeSubItem: "",
    openSubmenus: {},
  };
  setActiveItem(state.activeItem);
  setActiveSubItem(state.activeSubItem);
  setOpenSubmenus(state.openSubmenus);
  persistNavState(state.activeItem, state.activeSubItem, state.openSubmenus);
}

// Helper: Get color based on active state
const getActiveColor = (isActive) => {
  if (isActive) return "#403C6F";
  return "#FFFFFF";
};

// Helper: Get background color based on active state
const getActiveBackgroundColor = (isActive) => {
  if (isActive) return "#FFFFFF";
  return "transparent";
};

// Helper: Get border radius based on submenu state
const getBorderRadius = (hasSub, isOpen, isAnimating) => {
  if (!hasSub) return "8px";
  if (isOpen || isAnimating) return "8px 8px 0 0";
  return "8px";
};

// Helper: Render expand icon
const renderExpandIcon = (hasSub, isOpen, isActive) => {
  if (!hasSub) return null;
  const IconComponent = isOpen ? ExpandLess : ExpandMore;
  const iconColor = getActiveColor(isActive);
  return (
    <IconComponent
      sx={{ color: iconColor }}
      aria-hidden="true"
      focusable="false"
    />
  );
};

function NavigationSubmenu({
  item,
  submenuId,
  isOpen,
  setSubmenuAnimating,
  activeSubItem,
  handleSubItemClick,
}) {
  return (
    <ListItem component="li" disableGutters sx={{ p: 0, display: "block" }}>
      <Collapse
        in={isOpen}
        timeout="auto"
        unmountOnExit
        onEntering={() =>
          setSubmenuAnimating((prev) => ({ ...prev, [item.text]: true }))
        }
        onEntered={() =>
          setSubmenuAnimating((prev) => ({ ...prev, [item.text]: false }))
        }
        onExiting={() =>
          setSubmenuAnimating((prev) => ({ ...prev, [item.text]: true }))
        }
        onExited={() =>
          setSubmenuAnimating((prev) => {
            const copy = { ...prev };
            delete copy[item.text];
            return copy;
          })
        }
      >
        <List
          id={submenuId}
          component="ul"
          disablePadding
          dense
          aria-label={`Submenu: ${item.text}`}
          sx={{
            backgroundColor: "#FFFFFF",
            margin: 0,
            borderRadius: "0 0 8px 8px",
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
            padding: "0 0 4px 0",
            listStyle: "none",
            border: "1px solid #E0E0E0",
            borderTop: "none",
          }}
        >
          {item.subItems.map((subItem) => (
            <ListItem
              key={subItem.text}
              component="li"
              button
              onClick={() => handleSubItemClick(subItem, item)}
              sx={{
                pl: 4,
                py: "8px",
                backgroundColor:
                  activeSubItem === subItem.text ? "#F2F5FF" : "#FFFFFF",
                "&:focus-visible": {
                  outline: "2px solid #403C6F",
                  outlineOffset: "2px",
                },
                "&:hover": {
                  backgroundColor:
                    activeSubItem === subItem.text ? "#F2F5FF" : "#f9f9f9",
                },
                cursor: "pointer",
              }}
            >
              <ListItemText
                sx={{ ml: 1 }}
                primary={subItem.text}
                slotProps={{
                  primary: {
                    color: "#403C6F",
                    fontSize: "1rem",
                    fontWeight: 700,
                    lineHeight: "1.5",
                    letterSpacing: "0%",
                    textAlign: "left",
                    component: "span",
                  },
                }}
              />
            </ListItem>
          ))}
        </List>
      </Collapse>
    </ListItem>
  );
}

NavigationSubmenu.propTypes = {
  item: PropTypes.shape({
    text: PropTypes.string.isRequired,
    path: PropTypes.string,
    isClickable: PropTypes.bool,
    subItems: PropTypes.arrayOf(
      PropTypes.shape({
        text: PropTypes.string.isRequired,
        path: PropTypes.string,
      }),
    ),
    icon: PropTypes.element,
  }).isRequired,
  submenuId: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  setSubmenuAnimating: PropTypes.func.isRequired,
  activeSubItem: PropTypes.string,
  handleSubItemClick: PropTypes.func.isRequired,
};

function NavigationItem({
  item,
  activeItem,
  activeSubItem,
  openSubmenus,
  submenuAnimating,
  handleItemClick,
  handleSubItemClick,
  setSubmenuAnimating,
  navInstanceId,
  collapsed,
}) {
  const submenuId = `submenu-${item.text
    .replaceAll(/\s+/g, "-")
    .toLowerCase()}-${navInstanceId}`;
  const isActive = activeItem === item.text;
  const isOpen = !!openSubmenus[item.text];
  const hasSub = Array.isArray(item.subItems) && item.subItems.length > 0;
  const isAnimating = submenuAnimating[item.text];
  const activeColor = getActiveColor(isActive);
  const activeBgColor = getActiveBackgroundColor(isActive);
  const borderRadius = getBorderRadius(hasSub, isOpen, isAnimating);

  return (
    <React.Fragment key={item.text}>
      <ListItem
        component="li"
        button={item.isClickable}
        onClick={() => handleItemClick(item)}
        aria-expanded={hasSub && isOpen}
        aria-controls={hasSub ? submenuId : undefined}
        sx={{
          backgroundColor: activeBgColor,
          borderRadius: borderRadius,
          marginBottom: "0",
          px: collapsed ? 1 : 2,
          py: 1,
          justifyContent: collapsed ? "center" : "flex-start",
          cursor: item.isClickable ? "pointer" : "not-allowed",
          "&:focus-visible": {
            outline: "2px solid #FFFFFF",
            outlineOffset: "2px",
          },
          "&:hover": {
            backgroundColor: activeBgColor,
            "& .MuiListItemText-root span, & .MuiSvgIcon-root": {
              color: activeColor,
            },
          },
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: collapsed ? "25px" : "30px",
            mr: collapsed ? 0 : 1,
            color: activeColor,
            justifyContent: "center",
          }}
        >
          {React.cloneElement(item.icon, {
            isActive,
            style: { ...item.icon.props.style },
            "aria-hidden": true,
            focusable: false,
          })}
        </ListItemIcon>
        {!collapsed && (
          <ListItemText
            primary={item.text}
            slotProps={{
              primary: {
                fontWeight: isActive ? "bold" : "normal",
                color: activeColor,
                fontSize: "0.95rem",
                component: "span",
                sx: {
                  whiteSpace: "nowrap",
                },
              },
            }}
          />
        )}
        {!collapsed && renderExpandIcon(hasSub, isOpen, isActive)}
      </ListItem>
      {hasSub && !collapsed && (
        <NavigationSubmenu
          item={item}
          submenuId={submenuId}
          isOpen={isOpen}
          submenuAnimating={submenuAnimating}
          setSubmenuAnimating={setSubmenuAnimating}
          activeSubItem={activeSubItem}
          handleSubItemClick={handleSubItemClick}
        />
      )}
    </React.Fragment>
  );
}

NavigationItem.propTypes = {
  item: PropTypes.shape({
    text: PropTypes.string.isRequired,
    path: PropTypes.string,
    isClickable: PropTypes.bool,
    subItems: PropTypes.arrayOf(
      PropTypes.shape({
        text: PropTypes.string.isRequired,
        path: PropTypes.string,
      }),
    ),
    icon: PropTypes.element.isRequired,
  }).isRequired,
  activeItem: PropTypes.string,
  activeSubItem: PropTypes.string,
  openSubmenus: PropTypes.object.isRequired,
  submenuAnimating: PropTypes.object.isRequired,
  handleItemClick: PropTypes.func.isRequired,
  handleSubItemClick: PropTypes.func.isRequired,
  setSubmenuAnimating: PropTypes.func.isRequired,
  navInstanceId: PropTypes.string.isRequired,
  collapsed: PropTypes.bool,
};

export default function Navigation({
  onMobileClose,
  collapsed,
  onToggleCollapse,
  showCollapseToggle,
}) {
  const navigate = useNavigate();
  const navInstanceId = useId();

  const loadFromStorage = (key, defaultValue) => {
    try {
      const item = globalThis.localStorage.getItem(`nav_${key}`);
      return item ? JSON.parse(item) : defaultValue;
    } catch (error_) {
      return error_;
    }
  };

  const [activeItem, setActiveItem] = React.useState(
    loadFromStorage("activeItem", "Member Outreach"),
  );
  const [activeSubItem, setActiveSubItem] = React.useState(
    loadFromStorage("activeSubItem", ""),
  );
  const [openSubmenus, setOpenSubmenus] = React.useState(
    loadFromStorage("openSubmenus", {}),
  );

  const [submenuAnimating, setSubmenuAnimating] = useState({});

  React.useEffect(() => {
    try {
      localStorage.setItem("nav_activeItem", JSON.stringify(activeItem));
      localStorage.setItem("nav_activeSubItem", JSON.stringify(activeSubItem));
      localStorage.setItem("nav_openSubmenus", JSON.stringify(openSubmenus));
    } catch (error) {
      return `navigation state save failed: ${error.message}`;
    }
  }, [activeItem, activeSubItem, openSubmenus]);

  /**
   * Handles submenu item click
   * @param {Object} item - Navigation item with submenu
   * @param {string} item.text - Item text identifier (required)
   */
  const handleSubmenuClick = (item) => {
    setOpenSubmenus((prev) => ({ ...prev, [item.text]: !prev[item.text] }));
    setActiveItem(item.text);
    if (!openSubmenus[item.text]) setActiveSubItem("");
    persistNavState(item.text, openSubmenus[item.text] ? activeSubItem : "", {
      ...openSubmenus,
      [item.text]: !openSubmenus[item.text],
    });
  };

  const handleRegularItemClick = (item) => {
    setActiveItem(item.text);
    setActiveSubItem("");
    setOpenSubmenus({});
    persistNavState(item.text, "", {});
    if (item.path) {
      if (onMobileClose) onMobileClose();
      navigate(item.path);
    }
  };

  const handleItemClick = (item) => {
    if (!item.isClickable) return;
    const hasSub = Array.isArray(item.subItems) && item.subItems.length > 0;
    if (hasSub) {
      if (collapsed && typeof onToggleCollapse === "function") {
        onToggleCollapse();
      }
      handleSubmenuClick(item);
      return;
    }
    handleRegularItemClick(item);
  };

  useEffect(() => {
    const path = globalThis.location.pathname;
    const basename = process.env.REACT_APP_BASENAME || "/";
    const normalizedPath = path.startsWith(basename)
      ? path.substring(basename.length)
      : path;
    const navState =
      findActiveNavState(normalizedPath, menuItems) ||
      findActiveNavState(path, menuItems);
    if (navState) {
      setActiveItem(navState.activeItem);
      setActiveSubItem(navState.activeSubItem);
      setOpenSubmenus(navState.openSubmenus);
    }

    if (path.includes("/providerProfile/")) {
      setActiveItem("Provider Outreach");
      setActiveSubItem("");
      setOpenSubmenus({});
      return;
    } else if (path.includes("/pharmacyProfile/")) {
      setActiveItem("Pharmacy Outreach");
      setActiveSubItem("");
      setOpenSubmenus({});
      return;
    } else if (path.includes("/manual-tasks")) {
      setActiveItem("Manual Tasks");
      setActiveSubItem("");
      setOpenSubmenus({});
      return;
    }

    for (const item of menuItems) {
      if (!item.isClickable) {
        continue;
      }

      if (item.subItems) {
        const matchingSubItem = item.subItems.find(
          (subItem) => subItem.path === normalizedPath || subItem.path === path,
        );

        if (matchingSubItem) {
          setActiveItem(item.text);
          setActiveSubItem(matchingSubItem.text);
          setOpenSubmenus((prev) => ({
            ...prev,
            [item.text]: true,
          }));
        }
      } else if (item.path === normalizedPath || item.path === path) {
        setActiveItem(item.text);
        setActiveSubItem("");
      }
    }
  }, [globalThis.location.pathname]);

  const handleSubItemClick = (item, parentItem) => {
    setActiveItem(parentItem.text);
    setActiveSubItem(item.text);
    persistNavState(parentItem.text, item.text, {
      ...openSubmenus,
      [parentItem.text]: true,
    });
    if (!item.path) return;

    navigate(item.path);
    if (onMobileClose) onMobileClose();
  };

  const [sidebarScale, setSidebarScale] = useState(1);
  useEffect(() => {
    const inches = getScreenInches();
    if (inches < 14) {
      setSidebarScale(0.9);
    } else {
      setSidebarScale(1);
    }
  }, []);

  useEffect(() => {
    const handler = () =>
      applyMembersHighlight(setActiveItem, setActiveSubItem, setOpenSubmenus);
    globalThis.addEventListener("updateNavFromManualTasks", handler);
    globalThis.addEventListener("updateNavFromProfileOpportunities", handler);
    return () => {
      globalThis.removeEventListener("updateNavFromManualTasks", handler);
      globalThis.removeEventListener(
        "updateNavFromProfileOpportunities",
        handler,
      );
    };
  }, []);

  useEffect(() => {
    setOpenSubmenus((prev) => {
      const openKeys = Object.keys(prev).filter((k) => prev[k]);
      if (openKeys.length <= 1) return prev;

      if (activeItem && prev[activeItem]) {
        return { [activeItem]: true };
      }

      return { [openKeys[0]]: true };
    });
  }, [activeItem]);

  const moduleNames = [
    "Member Outreach",
    // "Provider Outreach",
    // "Pharmacy Outreach",
    // "Manual Tasks",
    // "Member Reporting",
    // "Supervisor Reporting",
    // "Invoice Reporting",
    "Role Management",
    "Dashboard",
    "CAG Configuration",
    "Assessment Configuration",
    "CG+ Assessment Templates",
  ];

  const permissionsMap = {};
  for (const name of moduleNames) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    permissionsMap[name] = usePermissions(name);
  }

  // SonarQube S6774: item.subItems, item.subItems.filter, item.text are validated by PropTypes.shape above
  // NOSONAR
  const filteredMenuItems = menuItems
    .map((item) => {
      if (!item.subItems) {
        return permissionsMap[item.text]?.canRead ? item : null;
      }

      // NOSONAR: item.subItems, item.subItems.filter are covered by PropTypes
      const subItems = item.subItems.filter(
        (sub) => permissionsMap[sub.text]?.canRead,
      );

      return subItems.length > 0 ? { ...item, subItems } : null;
    })
    .filter((item) => item !== null);

  React.useEffect(() => {
    const currentPath = globalThis.location.pathname;
    const state = buildNavStateForPath(currentPath);
    if (!state) return;
    setActiveItem(state.activeItem);
    setActiveSubItem(state.activeSubItem);
    setOpenSubmenus(state.openSubmenus);
    persistNavState(state.activeItem, state.activeSubItem, state.openSubmenus);
  }, []);

  const currentDrawerWidth = collapsed ? collapsedDrawerWidth : drawerWidth;
  let navListWidth = "100%";
  if (!collapsed && sidebarScale < 1) {
    navListWidth = "111%";
  }
  const toggleAriaLabel = collapsed ? "Expand sidebar" : "Collapse sidebar";

  return (
    <Box sx={{ display: "flex" }}>
      <Drawer
        variant="permanent"
        sx={{
          width: currentDrawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: currentDrawerWidth,
            boxSizing: "border-box",
            background: "#403C6F",
            color: "#FFFFFF",
            borderRight: "none",
            position: "fixed",
            top: 0,
            left: 0,
            height: "100vh",
            overflowY: "hidden",
            padding: "8px",
            overflowX: "hidden",
          },
        }}
      >
        <Toolbar
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            px: collapsed ? 1 : 2,
            py: 1,
            minHeight: "56px !important",
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          {!collapsed && <AppLogo />}
          {showCollapseToggle && (
            <IconButton
              onClick={onMobileClose || onToggleCollapse}
              aria-label={toggleAriaLabel}
              sx={{
                color: "#FFFFFF",
                position: "absolute",
                right: 8,
                top: "50%",
                transform: "translateY(-50%)",
                p: 1,
              }}
            >
              <MenuToggleIcon />
            </IconButton>
          )}
        </Toolbar>

        <Divider sx={{ bgcolor: "#E0E0E0", mx: -1, marginBottom: 2 }} />

        <List
          component="ul"
          tabIndex={0}
          aria-label="Sidebar navigation menu"
          sx={{
            transform: `scale(${sidebarScale})`,
            transformOrigin: "top left",
            width: navListWidth,
            minHeight: "100vh",
            overflowX: "hidden",
            listStyle: "none",
            padding: 0,
            margin: 0,
          }}
        >
          {filteredMenuItems.map((item) => (
            <NavigationItem
              key={item.text}
              item={item}
              activeItem={activeItem}
              activeSubItem={activeSubItem}
              openSubmenus={openSubmenus}
              submenuAnimating={submenuAnimating}
              handleItemClick={handleItemClick}
              handleSubItemClick={handleSubItemClick}
              setSubmenuAnimating={setSubmenuAnimating}
              navInstanceId={navInstanceId}
              collapsed={collapsed}
            />
          ))}
        </List>
      </Drawer>
    </Box>
  );
}

Navigation.propTypes = {
  onMobileClose: PropTypes.func,
  collapsed: PropTypes.bool,
  onToggleCollapse: PropTypes.func,
  showCollapseToggle: PropTypes.bool,
};

Navigation.defaultProps = {
  collapsed: false,
  onToggleCollapse: () => {},
  showCollapseToggle: true,
};
